// Step 4 — FloatingActionManager: the single owner of app-level floating
// action buttons. Route rules decide WHERE the camera FAB may exist;
// useFloatingActionSafeArea decides WHEN it must yield (focused inputs,
// open keyboard) so it never overlaps a DM composer, search field, or
// comment sheet mid-typing.
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { CameraActionSheet } from '../CameraActionSheet';
import { ICON_SIZE, ICON_STROKE } from '../../constants/iconTokens';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useFloatingActionSafeArea } from './useFloatingActionSafeArea';

// Surfaces where the FAB must never render at all: composers own their own
// attachment buttons, admin/settings flows have dense forms, and the social
// feed / fit-check surfaces have their own contextual camera CTAs (the FAB
// must never overlap the feed action rail).
// Exported for regression tests — the FAB must NEVER render on these routes.
export const HIDDEN_PATH_PREFIXES = [
  '/settings',
  '/account/delete',
  '/verify-id',
  '/admin',
  '/founder',
  '/messages',              // DM list + thread composer
  '/notifications/settings',
  '/profile/blocked-users',
  '/events',
  '/stylist/action',
  '/search',                // full-page search input
  '/social',                // feed action rail + "Share a look" header CTA
  '/fit-check',             // active capture surface
];

export const FloatingActionManager = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const { suppressed, bottomOffset } = useFloatingActionSafeArea();
  // Real kill switch — founders can disable the FAB globally.
  const { ready, on } = useFeatureFlag('floating_action_manager_v1');

  if (!user || !ready || !on) return null;
  if (HIDDEN_PATH_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Upload outfit photo or open camera"
        aria-hidden={suppressed}
        tabIndex={suppressed ? -1 : 0}
        data-testid="floating-camera-fab"
        className={`fixed right-4 md:right-10 h-14 w-14 md:w-auto justify-center md:justify-start md:pl-4 md:pr-5 bg-[#0D0D0D] text-white rounded-full flex items-center gap-2.5 shadow-2xl z-40 active:scale-95 border border-[#C7A96B]/50 transition-[opacity,transform] duration-200 ${
          suppressed ? 'opacity-0 translate-y-3 pointer-events-none' : 'opacity-100 translate-y-0'
        }`}
        style={{ bottom: bottomOffset, minWidth: '44px', minHeight: '44px' }}
      >
        <svg width={ICON_SIZE.lg} height={ICON_SIZE.lg} viewBox="0 0 24 24" fill="none"
          stroke="#C7A96B" strokeWidth={ICON_STROKE.regular} aria-hidden="true">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
        <span className="hidden md:inline font-sans text-[11px] uppercase tracking-[0.16em] text-white">Add photo</span>
      </button>
      <CameraActionSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default FloatingActionManager;
